
import { SOURCE_SYSTEM } from "@/constans";
import {
  Delegation,
  IClient,
  IClientData,
  IClientParams,
  ICountry,
  IDelegationData,
  ILimit,
  ISubclientData,
  ISubclientDetailData,
  ISubclientParams,
  IThirdcode,
  IUpdateSubclient,
  IUser,
} from "@/entities";
import { i18n } from "@/i18n";
import {
  IGenericResponse,
  IPageResponse,
  IValidateSolSubclientParams,
  IValidateSolSubclientResponse,
} from "@/services";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import Multiselect from "vue-multiselect";
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { debounce } from "lodash";
import ThirdcodeTable from "@/components/thirdcode/ThirdcodeTable.vue";

@Component({
  name: "subclient-add-page",
  components: {
    ValidationObserver,
    Multiselect,
    ThirdcodeTable,
  },
})
export default class SubclientAddPage extends Vue {
  @Action("fetchClientsPage") fetchClientsPage!: (
    params: IClientParams
  ) => Promise<IPageResponse<IClientData> | undefined>;
  @Action("fetchSubclientsPage") fetchSubclientsPage!: (
    params: ISubclientParams
  ) => Promise<IPageResponse<ISubclientData> | undefined>;
  @Action("saveUpdateSubclient") saveUpdateSubclient!: (
    updateSubclient: IUpdateSubclient
  ) => Promise<IGenericResponse<ISubclientDetailData> | undefined>;
  @Action("validateSolSubclient") validateSolSubclient!: (payload: {
    countryCode: string;
    params: IValidateSolSubclientParams;
  }) => Promise<IValidateSolSubclientResponse | undefined>;
  @Action("fetchDelegationsByCountry") fetchDelegationsByCountry!: (
    idCountry: number | undefined
  ) => Promise<IDelegationData[] | undefined>;
  @Getter("getLoggedUser") loggedUser?: IUser;
  @Getter("getCountriesPage") countriesPage?: IPageResponse<ICountry>;

  private pageNumber = 1;
  private pageSize = 10;

  private clientsPage: IClient[] = [];

  public validateInSolInput = true;
  public countrySelected: ICountry | null = null;
  public clientSelected: IClient | null = null;
  public sourceSystemSelected: string | null = null;
  public subclientCodeInput: string | null = null;
  private subclientCode?: string;
  private solSubclientExists = false;

  private subclientOid?: string;
  public subclientDescriptionInput: string | null = null;
  public subclientDelegationSelected: string | null = null;
  public subclientSmartCashIdInput: string | null = null;
  public visibleCountry = true;
  public optionsDelegations: string[] = [];
  public thirdcodeList: IThirdcode[] = [];
  public limitList: ILimit[] = [];

  constructor() {
    super();
  }

  get clientOptions(): IClient[] {
    return this.clientsPage.map((c) => {
      if (c.codeSol) {
        c.label = c.codeSol + " - " + c.description;
      } else {
        c.label = c.description;
      }
      return c;
    });
  }

  get isCodeDisabled(): boolean {
    if (this.validateInSolInput) {
      return !this.countrySelected || !this.clientSelected || !this.sourceSystemSelected;
    } else {
      return !this.countrySelected || !this.clientSelected;
    }
  }

  get showAdditionalFields(): boolean {
    const result = this.subclientCodeInput !== null && this.subclientCodeInput !== "";
    if (this.validateInSolInput) {
      return result && this.solSubclientExists;
    }
    return result;
  }

  async mounted(): Promise<void> {
    extend("subclient_exists", {
      validate: async () => {
        let countryValid;
        if (this.loggedUser?.admin) {
          countryValid = (
            await (this.$refs.country as InstanceType<typeof ValidationProvider>).validate()
          ).valid;
        }
        const sourceSystemValid = (
          await (this.$refs.sourceSystem as InstanceType<typeof ValidationProvider>).validate()
        ).valid;
        const clientValid = (
          await (this.$refs.client as InstanceType<typeof ValidationProvider>).validate()
        ).valid;

        if (this.loggedUser?.admin) {
          return countryValid && sourceSystemValid && clientValid ? this.validateCode() : true;
        } else {
          return sourceSystemValid && clientValid ? this.validateCode() : true;
        }
      },
      message: () => this.$t("validation.messages.subclientNotFound") as string,
    });
  }

  get optionsCountries(): ICountry[] {
    if (this.loggedUser?.admin) {
      this.visibleCountry = true;
      return this.countriesPage?.content || [];
    } else if (this.loggedUser?.country?.id) {
      this.visibleCountry = false;
      this.countrySelected = this.loggedUser.country;
      this.searchClients();

      return (
        this.countriesPage?.content.filter(
          (country) => this.loggedUser?.country?.id === country.id
        ) || []
      );
    }
    return [];
  }

  get optionsSourceSystem(): any {
    return [
      i18n.t("label.none"),
      SOURCE_SYSTEM.SIGII,
      SOURCE_SYSTEM.GENESIS,
      SOURCE_SYSTEM.CORPOINT,
      SOURCE_SYSTEM.PROFAT,
    ];
  }

  debouncedSearchClients = debounce(
    async (description?: string) => this.searchClients(description),
    500
  );
  async searchClients(description?: string): Promise<void> {
    this.clientsPage = [];

    if (this.countrySelected) {
      const res = await this.fetchClientsPage({
        page: 0,
        size: 10,
        countryId: this.countrySelected?.id,
        codeOrDesc: description,
        hasOid: this.validateInSolInput ? true : undefined,
      });

      if (res) {
        this.clientsPage = res.content;
      }
    }
  }
  async onSubmit(): Promise<void> {
    let delegation;
    if (this.subclientDelegationSelected?.includes("-")) {
      delegation = this.subclientDelegationSelected.split(" -")[0];
    } else {
      delegation = this.subclientDelegationSelected;
    }

    const res = await this.saveUpdateSubclient({
      oidSol: this.subclientOid || "",
      codeSol: this.validateInSolInput ? this.subclientCode! : this.subclientCodeInput!,
      uniqueCode: "",
      description: this.subclientDescriptionInput!,
      active: true,
      idSmartcash: this.subclientSmartCashIdInput!,
      clientId: this.clientSelected!.id!,
      delegation: delegation,
      thirdcodes: this.thirdcodeList,
      limits: this.limitList,
    });

    if (res?.status === 1) {
      let params: ISubclientParams = {};
      params.size = this.pageSize;
      params.page = this.pageNumber - 1;
      await this.fetchSubclientsPage(params);
      this.$router.back();
    }
  }

  async validateCode(): Promise<boolean> {
    const res = await this.validateSolSubclient({
      countryCode: this.countrySelected!.code!,
      params: {
        clientCode: this.clientSelected?.codeSol,
        centerCode: this.subclientCodeInput!,
        sourceSystem: this.sourceSystemSelected!,
      },
    });

    this.solSubclientExists = res ? res.exito : false;

    if (res?.exito) {
      this.optionsDelegations = [];
      this.subclientDescriptionInput = res?.centro.desCentro;

      res?.delegaciones.forEach((delegacion) => {
        if (delegacion.bolPrincipal) {
          this.optionsDelegations?.push(delegacion?.idSmartcash);
          this.subclientDelegationSelected = delegacion?.idSmartcash;
        }
      });
      this.subclientOid = res?.centro.idCentro;
      this.subclientCode = res?.centro.codCentro;
      this.thirdcodeList = res.centro.codigosTercero.map((e) => {
        return {
          system: e.sistemaTerceroCodigo,
          code: e.valor,
        } as IThirdcode;
      });
      const rest = await this.fetchDelegationsByCountry(this.countrySelected?.id);
      if (rest) {
        this.formatDelegations(rest);
      }

      return true;
    } else {
      this.optionsDelegations = [];
      this.subclientDescriptionInput = null;
      this.subclientDelegationSelected = null;
      this.subclientOid = undefined;
      this.subclientCode = undefined;
      this.thirdcodeList = [];
      return false;
    }
  }

  formatDelegations(delegations: Delegation[]): void {
    delegations.forEach((delegation) => {
      if (delegation?.idSmartcash) {
        if (!this.optionsDelegations.includes(delegation?.idSmartcash)) {
          this.optionsDelegations?.push(delegation?.idSmartcash + " - " + delegation?.description);
        } else {
          this.optionsDelegations.shift();
          this.optionsDelegations.push(delegation?.idSmartcash + " - " + delegation?.description);
          this.subclientDelegationSelected =
            delegation?.idSmartcash + " - " + delegation?.description;
        }
      }
    });
  }

  validateInSolInputChange(): void {
    this.sourceSystemSelected = null;
    this.subclientCodeInput = null;
    this.subclientCode = undefined;
    this.subclientOid = undefined;
    this.subclientDescriptionInput = null;
    this.subclientDelegationSelected = null;
    this.subclientSmartCashIdInput = null;
    this.thirdcodeList = [];
    this.limitList = [];
  }

  countrySelectedChange(): void {
    // Reset same fields
    this.validateInSolInputChange();
    this.clientSelected = null;

    this.searchClients();
  }

  subclientCodeInputChange(): void {
    this.subclientCode = undefined;
    this.subclientOid = undefined;
    this.subclientDescriptionInput = null;
    this.subclientDelegationSelected = null;
    this.subclientSmartCashIdInput = null;
    this.thirdcodeList = [];
    this.limitList = [];
  }

  async searchDelegations(): Promise<void> {
    this.optionsDelegations = [];
    const rest = await this.fetchDelegationsByCountry(this.countrySelected?.id);
    if (rest) {
      this.formatDelegations(rest);
    }
  }
}
